import { useCallback, useEffect, useMemo } from 'react'

import { useQueries, useQueryClient } from '@tanstack/react-query'
import { compact, groupBy } from 'lodash'

import {
  getListedChainIds,
  SupportedChainId,
  SupportedVmTypes,
} from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  RelayNetworkMetadata,
  useRelayNetworks,
} from '~/hooks/useRelayNetworks'
import { getMultipleTokenData } from '~/hooks/useTokenMetadata/getMultipleTokenData'
import { useTokenLogos } from '~/hooks/useTokenMetadata/useTokenLogos'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress, TokenContract } from '~/types'
import { Address } from '~/utils/address'
import { shortenAddress } from '~/utils/addresses'
import { fetchBatchTokenMetadata } from '~/utils/relay/fetchBatchTokenMetadata'
import { getReadPublicClient } from '~/utils/requests'
import { unwrapToken } from '~/utils/tokens'
import { getVaultAssetSymbol, isVaultToken } from '~/utils/vault'

export type TokenMetadata = {
  address: IAddress
  symbol: string
  decimals: number
  validToken: boolean
  logoUri: string
  isPosition: boolean
  tokenId?: string
  chainId: SupportedChainId
}

export type TokenMetadataWithPrice = TokenMetadata & {
  price: number | undefined
  isLoadingPrice: boolean
}

// This query key is used for the internal token metadata items
const getTokenMetadataQueryKey = (
  chainId: number,
  address: IAddress,
  tokenId?: string,
) => [
  QUERY_KEYS.TOKEN_METADATA,
  chainId,
  Address.format(address),
  ...(tokenId ? [tokenId] : []),
]

type ReceivedToken = {
  chainId: SupportedChainId
  address: IAddress
  tokenId?: string
  defaultSymbolToShortenAddress?: boolean
}

type LenientReceivedToken = Omit<ReceivedToken, 'address' | 'chainId'> & {
  chainId: SupportedChainId | undefined
  address: IAddress | undefined
}

type LenientReceivedTokens = Array<LenientReceivedToken>

// Function overloads
export function useMultipleTokensMetadata(
  tokens: LenientReceivedTokens,
  options: { includePrice: true },
): TokenMetadataWithPrice[]

export function useMultipleTokensMetadata(
  tokens: LenientReceivedTokens,
  options?: { includePrice?: false | undefined },
): TokenMetadata[]

/**
 * This hook is used to get the metadata for multiple tokens.
 *
 * @param tokens - An array of tokens to get the metadata for. Each token can have undefined chainId and address.
 * @param tokens[].chainId - The chain ID of the token (can be undefined)
 * @param tokens[].address - The address of the token (can be undefined)
 * @param tokens[].tokenId - The token ID of the token (for NFTs, optional)
 * @param tokens[].defaultSymbolToShortenAddress - Whether to shorten the address if the symbol is not provided (defaults to true)
 * @param options - The options for the request
 * @param options.includePrice - Whether to include the price of the tokens in the returned metadata.
 *
 * @returns An array of token metadata objects. If includePrice is true, each object will include a price field.
 */
export function useMultipleTokensMetadata(
  lenientTokens: LenientReceivedTokens,
  options: {
    includePrice?: boolean
  } = {},
): TokenMetadata[] | TokenMetadataWithPrice[] {
  // Normalise 0xEeee… warehouse sentinel to ADDRESS_ZERO so cache keys collide
  // across the two native-token spellings the backend may send (PE-6957).
  const tokens = lenientTokens
    .filter(
      (token): token is ReceivedToken =>
        token.chainId !== undefined && token.address !== undefined,
    )
    .map((token) => ({ ...token, address: unwrapToken(token.address) }))

  const queryClient = useQueryClient()
  const { addMultiplePendingPricingCalls, getPrice } = useTokenPrices()

  // Helper to find a token from relay networks
  const findRelayToken = useCallback(
    (
      relayNetworks: RelayNetworkMetadata[] | undefined,
      chainId: number,
      address: string,
    ) => {
      return relayNetworks
        ?.find((network) => network.id === chainId)
        ?.featuredTokens.find((ft) => Address.equal(ft.address, address))
    },
    [],
  )

  // Gate is global ("do we have RPC for this chain?"), not per-org —
  // a chain we support but the active org doesn't use is still an
  // on-chain multicall, not a Relay fallback.
  const hasRelayOnlyChains = useMemo(() => {
    const listedChainIds = getListedChainIds()
    return tokens.some((token) => !listedChainIds.includes(token.chainId))
  }, [tokens])

  const { data: relayNetworks } = useRelayNetworks({
    enabled: hasRelayOnlyChains,
    vmTypes: SupportedVmTypes,
  })

  // Fetch logos for all tokens using the batched hook
  const { data: tokenLogos } = useTokenLogos(
    tokens.map((token) => ({
      address: token.address,
      chainId: token.chainId,
    })),
  )

  // Serialize tokens for pricing calls to avoid circular references
  // when adding pending pricing calls
  const serializedTokensForPricingCalls = JSON.stringify(
    tokens.map((token) => ({
      chainId: token.chainId,
      address: token.address,
      tokenId: token.tokenId,
    })),
  )

  useEffect(
    function loadPendingPriceCalls() {
      const tokensForPricingCalls = JSON.parse(
        serializedTokensForPricingCalls,
      ) as Array<{
        chainId: SupportedChainId
        address: IAddress
        tokenId?: string
      }>

      addMultiplePendingPricingCalls(tokensForPricingCalls)
    },
    [addMultiplePendingPricingCalls, serializedTokensForPricingCalls],
  )

  // Group tokens by chain for batch calls. Serializing so we can use it in useMemo.
  const serializedTokens = JSON.stringify(
    tokens.map((token) => ({
      address: token.address,
      chainId: token.chainId,
      tokenId: token.tokenId,
      defaultSymbolToShortenAddress: token.defaultSymbolToShortenAddress,
    })),
  )

  // Create batch queries for each chain
  const batchQueries = useMemo(() => {
    const tokensByChain = groupBy(
      JSON.parse(serializedTokens) as ReceivedToken[],
      'chainId',
    )

    const queries = Object.entries(tokensByChain).map(
      ([chainId, chainTokens]) => {
        const numericChainId = parseInt(chainId)

        return {
          queryKey: [
            QUERY_KEYS.LOAD_MULTIPLE_TOKEN_METADATA,
            numericChainId,
            chainTokens.map(
              (token) =>
                `${Address.format(token.address)}${
                  token.tokenId ? `-${token.tokenId}` : ''
                }`,
            ),
          ],
          queryFn: async () => {
            // Check which tokens are already cached
            const cachedTokens = chainTokens.map((token) => {
              const individualKey = getTokenMetadataQueryKey(
                numericChainId,
                token.address,
                token.tokenId,
              )
              return {
                token,
                cachedData: queryClient.getQueryData(individualKey),
              }
            })

            // Filter tokens that need to be fetched
            const tokensToFetch = cachedTokens
              .filter(({ cachedData }) => cachedData === undefined)
              .map(({ token }) => token)

            let fetchedResults: Array<
              TokenContract | undefined | TokenMetadata
            > = []

            if (tokensToFetch.length > 0) {
              const isRelayNetwork =
                !getListedChainIds().includes(numericChainId)

              if (isRelayNetwork) {
                // Use Relay API for relay network tokens
                const relayMetadata = await fetchBatchTokenMetadata(
                  tokensToFetch.map((token) => ({
                    chainId: numericChainId,
                    address: token.address,
                    tokenId: token.tokenId,
                  })),
                  queryClient,
                )

                // Map relay metadata back to match tokensToFetch order
                fetchedResults = tokensToFetch.map((token) => {
                  return relayMetadata.find(
                    (meta) =>
                      Address.equal(meta.address, token.address) &&
                      meta.chainId === numericChainId,
                  )
                })

                // Cache the fetched results
                tokensToFetch.forEach((token, index) => {
                  const individualKey = getTokenMetadataQueryKey(
                    numericChainId,
                    token.address,
                    token.tokenId,
                  )
                  queryClient.setQueryData(individualKey, fetchedResults[index])
                })
              } else {
                // Use multicall for active chain tokens
                const publicClient = getReadPublicClient(numericChainId)
                fetchedResults = await getMultipleTokenData(
                  tokensToFetch,
                  numericChainId,
                  { publicClient },
                )

                // Cache the fetched results
                tokensToFetch.forEach((token, index) => {
                  const individualKey = getTokenMetadataQueryKey(
                    numericChainId,
                    token.address,
                    token.tokenId,
                  )
                  queryClient.setQueryData(individualKey, fetchedResults[index])
                })
              }
            }

            // Merge cached and fetched data in the correct order
            const mergedResults = compact(
              chainTokens.map(
                (token): TokenContract | undefined | TokenMetadata => {
                  const cachedToken = cachedTokens.find(
                    ({ token: t }) =>
                      t.address === token.address &&
                      t.tokenId === token.tokenId,
                  )
                  if (cachedToken?.cachedData !== undefined) {
                    return cachedToken.cachedData as
                      TokenContract | undefined | TokenMetadata
                  }

                  // Find the fetched result for this token
                  const fetchIndex = tokensToFetch.findIndex(
                    (t) =>
                      t.address === token.address &&
                      t.tokenId === token.tokenId,
                  )
                  return fetchIndex !== -1
                    ? fetchedResults[fetchIndex]
                    : undefined
                },
              ),
            )

            return mergedResults
          },
          enabled: chainTokens.length > 0,
          initialData: () => {
            // Check which tokens are already cached
            const cachedTokens = chainTokens.map((token) => {
              const individualKey = getTokenMetadataQueryKey(
                numericChainId,
                token.address,
                token.tokenId,
              )
              return {
                token,
                cachedData: queryClient.getQueryData(individualKey),
              }
            })

            const allCached = cachedTokens.every(
              ({ cachedData }) => cachedData !== undefined,
            )

            if (allCached) {
              // Return cached data for all tokens
              return cachedTokens.map(({ cachedData }) => cachedData)
            }

            return undefined
          },
          staleTime: Infinity,
          gcTime: Infinity,
        }
      },
    )

    return queries
  }, [queryClient, serializedTokens])

  const batchResults = useQueries({ queries: batchQueries })
  const tokensByChain = groupBy(tokens, 'chainId')

  const metadataItems = tokens.map(
    (
      { chainId, address, tokenId, defaultSymbolToShortenAddress = true },
      index,
    ) => {
      const tokenLogo = tokenLogos?.[index]
      const metadataQueryKey = getTokenMetadataQueryKey(
        chainId,
        address,
        tokenId,
      )
      const cachedTokenMetadata = queryClient.getQueryData<
        TokenMetadata | TokenContract | undefined
      >(metadataQueryKey)

      if (
        cachedTokenMetadata &&
        'validToken' in cachedTokenMetadata &&
        cachedTokenMetadata.validToken !== undefined
      ) {
        return {
          ...cachedTokenMetadata,
          logoUri: tokenLogo || cachedTokenMetadata.logoUri || '',
        }
      }

      // Check if this token is from a relay network
      const relayNetwork = relayNetworks?.find(
        (network) => network.id === chainId,
      )
      if (relayNetwork) {
        const relayToken = relayNetwork.featuredTokens.find((ft) =>
          Address.equal(ft.address, address),
        )
        if (relayToken) {
          const metadata = {
            address,
            symbol: relayToken.symbol,
            decimals: relayToken.decimals,
            validToken: true,
            logoUri: tokenLogo || relayToken.metadata.logoURI,
            isPosition: false,
            tokenId,
            chainId,
          }

          queryClient.setQueryData(
            getTokenMetadataQueryKey(chainId, address, tokenId),
            metadata,
          )

          return metadata
        }
      }

      const chainIndex = Object.keys(tokensByChain).indexOf(chainId.toString())
      const batchResult = batchResults[chainIndex]

      if (batchResult.data) {
        const tokenIndex = tokensByChain[chainId].findIndex(
          (token) => token.address === address && token.tokenId === tokenId,
        )
        const tokenData = batchResult.data[tokenIndex]
        const isPosition = isVaultToken(chainId, Address.format(address))

        // Check relay network for logo fallback
        const relayBatchToken = findRelayToken(relayNetworks, chainId, address)

        return {
          address,
          symbol:
            (isPosition
              ? getVaultAssetSymbol(chainId, Address.format(address))
              : tokenData?.symbol) ||
            (defaultSymbolToShortenAddress ? shortenAddress(address) : address),
          decimals: tokenData?.decimals ?? 18,
          validToken: tokenData?.decimals !== undefined,
          logoUri: tokenLogo || relayBatchToken?.metadata.logoURI || '',
          isPosition,
          tokenId,
          chainId,
        }
      }

      // Return default metadata while loading
      // Check relay network as fallback for logo
      const relayFallbackToken = findRelayToken(relayNetworks, chainId, address)

      return {
        address,
        symbol: shortenAddress(address),
        decimals: 18,
        validToken: false,
        logoUri: tokenLogo || relayFallbackToken?.metadata.logoURI || '',
        isPosition: false,
        tokenId,
        chainId,
      }
    },
  )

  if (options.includePrice) {
    return metadataItems.map((metadata) => {
      const price = getPrice(metadata.chainId, metadata.address)

      return {
        ...metadata,
        price,
        isLoadingPrice: false,
      }
    })
  }

  return metadataItems
}

export function useTokenMetadata(
  token: LenientReceivedToken & { includePrice: true },
): TokenMetadataWithPrice

export function useTokenMetadata(
  token: LenientReceivedToken & {
    includePrice?: false
  },
): TokenMetadata

/**
 * This hook is used to get the metadata for a single token.
 *
 * @param token.chainId - The chain ID of the token.
 * @param token.address - The address of the token.
 * @param token.tokenId - The token ID of the token (for NFTs).
 * @param token.defaultSymbolToShortenAddress - Whether to shorten the address
 * if the symbol is not provided.
 * @param token.includePrice - Whether to include the price of the token.
 *
 * @returns The metadata for the token. If includePrice is true, the returned
 * object will include a price field.
 */
export function useTokenMetadata(
  token: LenientReceivedToken & { includePrice?: boolean },
): TokenMetadata {
  const {
    chainId,
    address,
    tokenId,
    defaultSymbolToShortenAddress = true,
    includePrice = false,
  } = token

  const results = (
    useMultipleTokensMetadata as (
      tokens: LenientReceivedTokens,
      options: { includePrice?: boolean },
    ) => TokenMetadata[] | TokenMetadataWithPrice[]
  )(
    [{ chainId, address, tokenId, defaultSymbolToShortenAddress }],
    includePrice ? { includePrice: true } : {},
  )
  return results[0]
}

/**
 * This hooks returns a function that can be used to get metadata inside loops
 * of unknown length because it's impossible to use the useTokenMetadata hook
 * inside a loop, as it would break the rules of hooks.
 *
 * @warning Calling the returned function won't load the metadata for this token,
 * only what's already in the cache.
 *
 * @returns A function that returns the metadata for a token.
 */
export const useGetTokenMetadata = () => {
  const queryClient = useQueryClient()

  return useCallback(
    (token: LenientReceivedToken): TokenMetadata | undefined => {
      if (!token.chainId || !token.address) {
        return undefined
      }

      const queryKey = getTokenMetadataQueryKey(
        token.chainId,
        token.address,
        token.tokenId,
      )

      return queryClient.getQueryData(queryKey)
    },
    [queryClient],
  )
}

export const useLoadMultipleTokenMetadata = () => {
  const queryClient = useQueryClient()

  return useCallback(
    (tokens: ReceivedToken[]) => {
      tokens.forEach((token) => {
        queryClient.setQueryData(
          getTokenMetadataQueryKey(token.chainId, token.address, token.tokenId),
          token,
        )
      })
    },
    [queryClient],
  )
}
